'use client';

import type { ChildCartArticleType } from 'services/types';
import { formatPrice, type LocaleIso } from 'utils';
import { useCartStore, type CartStore } from 'stores/cart';
import { getChildArticlePriceDetails } from '../../../../utils';
import { CartPrice } from '../../../../../partials/CartPrice';

export interface ArticlePriceProps {
  articleId: number;
  price: string;
  childArticles: ChildCartArticleType[];
  locale: LocaleIso;
  className?: string;
}

/**
 * Thin wrapper around CartPrice scoped to a specific article.
 * The .price CSS class provides layout (min-width, text-align, font).
 * CartPrice handles the skeleton when this article's quantity is mutating.
 */
export function ArticlePrice({ articleId, price, childArticles, locale, className }: ArticlePriceProps) {
  const liveValues = useCartStore((s: CartStore) => s.cartLiveValues);
  const liveArticlePrice = liveValues?.articlePrices[articleId];

  const liveDetailsPrice = childArticles.reduce((total, child) => {
    const childQuantity = liveValues?.childAmounts[child.id] ?? child.quantity;
    const { totalPriceChildArticle } = getChildArticlePriceDetails(child, childQuantity);

    return total + totalPriceChildArticle;
  }, 0);

  const liveValue =
    liveArticlePrice === undefined ? undefined : (formatPrice(liveArticlePrice + liveDetailsPrice, locale) ?? price);

  return <CartPrice value={price} articleId={articleId} liveValue={liveValue} locale={locale} className={className} />;
}
